import { Fragment } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import OrganizationIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import RoadmapIcon from '@mui/icons-material/FormatListNumbered';
import StatusReportIcon from '@mui/icons-material/Assessment';
import FindingsIcon from '@mui/icons-material/BugReport';
import RunTestIcon from '@mui/icons-material/PlayArrow';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { useActiveOrgStore, useUIStore } from '@store/index';

const DRAWER_WIDTH = 280;

interface NavigationItem {
  path: string;
  labelKey: string;
  icon: React.ReactNode;
  section?: string;
  disabled?: boolean;
  disabledLabelKey?: string;
}

function NavigationDrawer() {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // REV-724: two separate flags, because the drawer is two different things.
  // `sidebarOpen` is the desktop panel that pushes content aside (persisted).
  // `mobileNavOpen` is the narrow-viewport modal that covers it, and starts
  // closed every time — an overlay is something the user asks for.
  const { sidebarOpen, setSidebarOpen, mobileNavOpen, setMobileNavOpen } = useUIStore();
  const { activeOrg } = useActiveOrgStore();

  // Remediation is org-scoped: route it at the currently-selected org's slug
  // when one is available; otherwise keep the entry visible but disabled so
  // users see the feature exists. (Findings is a global surface — REV-456.)
  const remediationPath = activeOrg ? `/orgs/${activeOrg.slug}/remediation` : '/orgs';

  // REV-589: the org's tracked places, for whichever org the header has
  // selected. Disabled rather than hidden when there is none, so the feature
  // stays discoverable — same treatment as Remediation above.
  const trackedPlacesPath = activeOrg ? `/orgs/${activeOrg.slug}/contexts` : '/orgs';

  // REV-897: the organization status report, for the org the header has
  // selected. Same disabled-not-hidden treatment as Remediation.
  const statusReportPath = activeOrg ? `/orgs/${activeOrg.slug}/reports/status` : '/orgs';

  const navigationItems: NavigationItem[] = [
    { path: '/', labelKey: 'navigation.home', icon: <HomeIcon /> },
    {
      path: '/tests/new',
      labelKey: 'navigation.runTest',
      icon: <RunTestIcon />,
      section: 'testing',
      // REV-681: with no organization, everything but Home and Settings is
      // unavailable. The page itself still copes with no org (it says so
      // inline), but the nav no longer sends people there to read that.
      disabled: !activeOrg,
      disabledLabelKey: 'navigation.runTestPickOrg',
    },
    {
      // REV-589: this used to be 'Organizations', pointing at the list. The
      // header org switcher is now the single mechanism for choosing one, and
      // the header menu already carries "Manage Organizations" for the
      // administrative case — a second, competing entry here just invited the
      // user to switch org in two different places. Tracked Places is what
      // people actually came looking for.
      path: trackedPlacesPath,
      labelKey: 'navigation.trackedPlaces',
      icon: <OrganizationIcon />,
      section: 'manage',
      disabled: !activeOrg,
      disabledLabelKey: 'navigation.trackedPlacesPickOrg',
    },
    {
      // REV-456: Findings is a global surface — the org + tracked place are
      // chosen on the page, so the path does not depend on the active org.
      // REV-681: it is still unavailable with no organization at all, since
      // there is nothing to choose on that page.
      path: '/findings',
      labelKey: 'navigation.findings',
      icon: <FindingsIcon />,
      section: 'workspace',
      disabled: !activeOrg,
      disabledLabelKey: 'navigation.findingsPickOrg',
    },
    {
      path: remediationPath,
      labelKey: 'navigation.remediation',
      icon: <RoadmapIcon />,
      section: 'workspace',
      disabled: !activeOrg,
      disabledLabelKey: 'navigation.remediationPickOrg',
    },
    {
      path: statusReportPath,
      labelKey: 'navigation.statusReport',
      icon: <StatusReportIcon />,
      section: 'workspace',
      disabled: !activeOrg,
      disabledLabelKey: 'navigation.statusReportPickOrg',
    },
    {
      path: '/settings',
      labelKey: 'navigation.settings',
      icon: <SettingsIcon />,
      section: 'settings',
    },
  ];

  // REV-679: the <a href> performs the navigation now, so this only has to
  // close the drawer behind it on mobile. It no longer calls navigate() —
  // doing both would be a double navigation, and swallowing the click would
  // break cmd/ctrl-click, middle-click and "open in new tab", which is half
  // the reason a link has to be a link.
  const handleNavigation = () => {
    if (isMobile) {
      setMobileNavOpen(false);
    }
  };

  const handleClose = () => {
    if (isMobile) {
      setMobileNavOpen(false);
    } else {
      setSidebarOpen(false);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      role="navigation"
      aria-label={t('navigation.sidebar')}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 2,
          py: 1.5,
          minHeight: 64,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {/* REV-570: the product name is NOT repeated here. It lives once, in
            the header/banner. Rendering it in both places meant a screen-reader
            user met it twice before reaching anything specific to the page.
            The Box stays: its 64px min-height aligns the drawer's first nav
            item with the AppBar beside it, and on mobile it holds the close
            control. */}
        {isMobile && (
          <IconButton onClick={handleClose} aria-label={t('navigation.hideSidebar')} size="small">
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation Items */}
      <List sx={{ flex: 1, pt: 1 }}>
        {navigationItems.map((item, index) => {
          // REV-680: an unavailable entry is never the current page.
          //
          // The org-scoped entries fall back to '/orgs' when no org is active
          // (the same collision the React key comment below is about), so on
          // /orgs their path matched the location and they rendered as
          // SELECTED — white on the primary background, indistinguishable
          // from the page you are actually on, and visibly different from how
          // the very same disabled entries look on /. Three entries also
          // claimed aria-current="page" at once, which is not a thing.
          //
          // Being unavailable and being current are mutually exclusive, so
          // say so once here rather than patching the styling downstream.
          const isActive = !item.disabled && location.pathname === item.path;
          const prevItem = navigationItems[index - 1];
          const showDivider = index > 0 && prevItem && item.section !== prevItem.section;

          return (
            // Key on the stable labelKey, not item.path: the org-scoped
            // entries fall back to '/orgs' when no org is active, colliding
            // with the Organizations item's key and leaving a stale duplicate
            // Findings entry once an org was selected (REV-398).
            // REV-525 (LANG-25): a Fragment, not a Box. A <div> between the
            // List's <ul> and the <li> makes the list item a non-direct child,
            // which is invalid list markup and breaks list semantics for screen
            // readers. The Divider is a <li role="separator"> for the same reason.
            <Fragment key={item.labelKey}>
              {showDivider && <Divider component="li" sx={{ my: 1 }} />}
              <ListItem disablePadding>
                {/* REV-679: these entries navigate to a URL, so they have to
                    BE links — not a div carrying a click handler, which is
                    what ListItemButton renders by default and what shipped
                    here (measured: <div role="button">, eight of them, zero
                    links). The rule, from the ticket and now CLAUDE.md:
                    navigates to a new URL -> <a>; does not -> <button>;
                    never a div or span with an onClick.

                    A disabled entry navigates nowhere, so by that same rule it
                    is a real <button> rather than a link with its href taken
                    away. And it stays focusable via aria-disabled instead of
                    the `disabled` attribute: `disabled` sets tabindex="-1", so
                    the very explanation of why the entry is unavailable was
                    unreachable by keyboard. That is the same reasoning as the
                    "never pre-disable submit buttons" rule in CLAUDE.md — a
                    control the user cannot reach cannot tell them anything. */}
                <ListItemButton
                  {...(item.disabled
                    ? {
                        component: 'button' as const,
                        'aria-disabled': true,
                      }
                    : {
                        component: RouterLink,
                        to: item.path,
                        onClick: handleNavigation,
                      })}
                  selected={isActive}
                  aria-current={isActive ? 'page' : undefined}
                  aria-describedby={
                    item.disabled && item.disabledLabelKey
                      ? `${item.labelKey}-why-disabled`
                      : undefined
                  }
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    // REV-681: aria-disabled changes nothing visually on its
                    // own, so unavailable entries looked exactly like working
                    // ones. Secondary text still clears 4.5:1 in both themes;
                    // the cursor and the missing hover say "not clickable"
                    // without relying on colour alone.
                    '&[aria-disabled="true"]': {
                      color: 'text.secondary',
                      cursor: 'not-allowed',
                      '&:hover': { backgroundColor: 'transparent' },
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActive ? 'inherit' : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 400,
                    }}
                  />
                </ListItemButton>
                {/* REV-679: the reason lived in a `title`, which is not
                    reliably announced and was never reachable on a control
                    that `disabled` had already taken out of the tab order.

                    OUTSIDE the button, not inside it. Nested, it became part
                    of the control's accessible name — "Tracked Places Choose
                    an organization…" — which broke name-based queries and
                    would have made every screen reader read the explanation as
                    part of the label. An existing test caught that. */}
                {item.disabled && item.disabledLabelKey && (
                  <Box component="span" id={`${item.labelKey}-why-disabled`} sx={visuallyHidden}>
                    {t(item.disabledLabelKey)}
                  </Box>
                )}
              </ListItem>
            </Fragment>
          );
        })}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t('navigation.version', { version: '1.0.0' })}
        </Typography>
      </Box>
    </Box>
  );

  // Mobile: Temporary drawer that overlays content
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileNavOpen}
        onClose={handleClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: Persistent drawer
  return (
    <Drawer
      variant="persistent"
      open={sidebarOpen}
      sx={{
        width: sidebarOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export { NavigationDrawer, DRAWER_WIDTH };
export default NavigationDrawer;
